/**
 * Sentence segmentation for "Arrange script" (F-60) — PURE logic (no React,
 * no DOM, no Yjs).
 *
 * THE invariant (the whole point of the feature — the script is the asset):
 * `segmentSentences` returns RAW segments that exactly partition the input —
 * `raw.join("") === text`, character for character. No word, punctuation mark
 * or internal space is ever added, dropped or altered; every transformation
 * downstream (preview display, merge-with-next, the final cell pieces) is a
 * concatenation of these raw segments plus a trim at the OUTER edges of each
 * piece. Whitespace BETWEEN pieces (spaces/newlines separating sentences) is
 * the only thing the split consumes, because it stops being needed once the
 * sentences live in separate cells.
 *
 * Boundaries come from `Intl.Segmenter` (granularity "sentence", UAX #29 —
 * handles `.` `?` `!` `。` `？` `！`, decimals, ellipses), with hard newlines
 * treated as additional boundaries (an author's line break in a script cell is
 * an intended break). Whitespace-only segments/lines attach to their
 * neighbour so no piece is ever blank.
 */

/**
 * Split `text` into raw sentence segments. Guarantees `result.join("") ===
 * text` exactly. Empty text → `[]`; a single sentence → one segment; when
 * `Intl.Segmenter` is unavailable the text is returned whole (no split is
 * always safe — the feature degrades to "nothing to arrange").
 */
export function segmentSentences(text: string, locale?: string): string[] {
  if (text.length === 0) return [];
  if (typeof Intl === "undefined" || typeof Intl.Segmenter !== "function") return [text];
  const segmenter = new Intl.Segmenter(locale, { granularity: "sentence" });

  const out: string[] = [];
  // Lines with their trailing newline run attached: "a\n\nb" → ["a\n\n", "b"].
  const lines = text.match(/[^\n]*\n+|[^\n]+/g) ?? [];
  for (const chunk of lines) {
    const newlines = /\n+$/.exec(chunk)?.[0] ?? "";
    const line = newlines.length > 0 ? chunk.slice(0, -newlines.length) : chunk;

    const segments =
      line.length > 0 ? Array.from(segmenter.segment(line), (s) => s.segment) : [];
    if (segments.length === 0) {
      // Empty line (only newlines): attach to the previous segment so the
      // partition stays exact; a leading empty line starts the first segment.
      appendToLast(out, newlines);
      continue;
    }

    // Whitespace-only segments carry no sentence — fold them into a neighbour.
    const merged: string[] = [];
    for (const segment of segments) {
      if (segment.trim().length === 0 && merged.length > 0) {
        merged[merged.length - 1] += segment;
      } else {
        merged.push(segment);
      }
    }
    while (merged.length > 1 && (merged[0] as string).trim().length === 0) {
      merged[1] = (merged[0] as string) + (merged[1] as string);
      merged.shift();
    }

    merged[merged.length - 1] += newlines; // the line's own break rides along
    out.push(...merged);
  }
  return out;
}

function appendToLast(out: string[], raw: string): void {
  if (raw.length === 0) return;
  if (out.length > 0) out[out.length - 1] += raw;
  else out.push(raw);
}

/**
 * The length past which a single sentence is "too long" to read as one spoken
 * script/subtitle row, and is a candidate for FURTHER splitting: at clause
 * marks by `segmentClauses` below (deterministic, free), and — when there is
 * no clause mark to cut on — by the AI fallback (ai-suggest.ts, which imports
 * this same number so the two never disagree on what "long" means).
 */
export const LONG_SENTENCE_MIN_CHARS = 60;

/**
 * A clause boundary we may cut a long sentence AFTER: the comma family and its
 * CJK equivalents, plus semicolon and colon. The mark itself STAYS with the
 * clause to its left — dropping it would change the script — so only the run of
 * whitespace after it is consumed at trim time, exactly as sentence boundaries
 * are. Sentence terminators (`. ? !`) are NOT here: those are the pure
 * `segmentSentences` path's job, and `.` must stay attached to decimals.
 */
const CLAUSE_DELIMITER = /[,;:，、；：]/;

/**
 * Don't strand a sliver: a cut must leave at least this many non-space
 * characters on the clause to its left AND in the remainder to its right.
 * Matches the server split's MIN_PIECE_CHARS, and it is what keeps "12:30",
 * "Hi, ok", and a trailing "…, D." from being cut into unreadable fragments.
 */
const MIN_CLAUSE_CHARS = 8;

function isAsciiDigit(ch: string | undefined): boolean {
  return ch !== undefined && ch >= "0" && ch <= "9";
}

/**
 * Split ONE sentence at clause boundaries (commas etc.), but only when it is
 * `minLen`+ characters long — a short sentence is one readable row and is
 * returned unchanged as `[sentence]`. Same EXACT-PARTITION guarantee as
 * `segmentSentences`: `result.join("") === sentence`, so no word or mark is
 * ever added, dropped or altered; a cut only decides WHERE a row ends. Feeds
 * the identical `raw`/`breaks` preview shape, so the dialog's merge toggles
 * work on clause cuts exactly as on sentence cuts.
 *
 * `sentence` may carry trailing whitespace/newlines from `segmentSentences`
 * (they ride along in the final piece); pass a raw sentence segment straight in.
 */
export function segmentClauses(sentence: string, minLen = LONG_SENTENCE_MIN_CHARS): string[] {
  if (sentence.trim().length < minLen) return [sentence];

  const out: string[] = [];
  let start = 0; // where the current clause begins in `sentence`
  let i = 0;
  while (i < sentence.length) {
    const ch = sentence[i] as string;
    if (CLAUSE_DELIMITER.test(ch)) {
      // A comma between two digits is a thousands separator (1,000), not a
      // clause break — same spirit as never splitting a decimal.
      if (
        (ch === "," || ch === "，") &&
        isAsciiDigit(sentence[i - 1]) &&
        isAsciiDigit(sentence[i + 1])
      ) {
        i++;
        continue;
      }
      // Take the mark and the whitespace after it into the LEFT clause, so the
      // next clause starts on its first real character (trim-clean, exact join).
      let end = i + 1;
      while (end < sentence.length && /\s/.test(sentence[end] as string)) end++;
      const clause = sentence.slice(start, end);
      const remainder = sentence.slice(end);
      // Only cut when BOTH sides carry real content.
      if (
        clause.trim().length >= MIN_CLAUSE_CHARS &&
        remainder.trim().length >= MIN_CLAUSE_CHARS
      ) {
        out.push(clause);
        start = end;
        i = end;
        continue;
      }
    }
    i++;
  }
  out.push(sentence.slice(start));
  return out;
}

/**
 * Assemble the final cell pieces from raw segments and the preview's boundary
 * choices. `breaks[i]` = "split after segment i" (length `raw.length - 1`);
 * a cleared break (merge-with-next) concatenates the raw segments, preserving
 * their internal whitespace exactly. Each piece is trimmed only at its outer
 * edges; a piece that trims to nothing (whitespace-only) is dropped.
 */
export function piecesForBreaks(
  raw: readonly string[],
  breaks: readonly boolean[],
): string[] {
  const pieces: string[] = [];
  let current = "";
  for (let i = 0; i < raw.length; i++) {
    current += raw[i];
    if (i === raw.length - 1 || breaks[i] === true) {
      const trimmed = current.trim();
      if (trimmed.length > 0) pieces.push(trimmed);
      current = "";
    }
  }
  return pieces;
}

/**
 * Raw segments from explicit CUT OFFSETS instead of `Intl.Segmenter` — the AI
 * path (features/arrange/ai-suggest.ts) for a cell that is one long sentence
 * with no punctuation to split on. `offsets` are character indices into
 * `text` (each cut falls between `offset-1` and `offset`); the server has
 * already validated them are ascending, in range, and well-spaced
 * (server split-sentence's `validateBreaks`) — this function trusts that and
 * does the literal slicing.
 *
 * Same word-preservation guarantee as `segmentSentences`: `result.join("")
 * === text` exactly, because every piece is a direct substring of the
 * original — there is no text from the model anywhere in the output, only
 * where it suggested to CUT. Feeds the identical `raw`/`breaks` preview shape
 * as the pure-logic path, so the rest of the dialog (merge toggles,
 * `piecesForBreaks`) is unaware of which path produced it.
 */
export function rawFromOffsets(text: string, offsets: readonly number[]): string[] {
  const raw: string[] = [];
  let start = 0;
  for (const cut of offsets) {
    raw.push(text.slice(start, cut));
    start = cut;
  }
  raw.push(text.slice(start));
  return raw;
}
